



















































































import { get, map, uniq, cloneDeep } from 'lodash';
import axios from 'axios';
import { PropType } from 'vue';
import { User, Event, MarketItem, Page } from '@/types/GlobalTypes';
import CoHeadline from '../../Atoms/co-headline/CoHeadline.vue';
import CoEvent from '../../Molecules/co-event/CoEvent.vue';
import CoMarketItem from '../../Molecules/co-marketitem/coMarketItem.vue';
import CoPage from '../../Molecules/co-page/CoPage.vue';
import CoLinkPreview from '../co-link-preview/CoLinkPreview.vue';

interface Fetch {
    ID?: string;
    Link?: string;
    Slug?: string;
    Type?: string;
    Url?: string;
}

interface PreviewPost {
    ID?: string;
    Type?: string;
    AuthorID?: string;
    CreatedBy?: string;
    UserID?: string;
    Author?: User | null;
    Text?: string;
    Slug?: string;
    CreatedAt?: string | number;
    UpdatedAt?: string | number;
    Images?: Array<{ ImageURL: string } | string>;
    ImageURLs?: string[];
    ParentID?: string;
    ParentType?: number | string;
    ParentContent?: { Fetch?: { ID: string; Type: string } } | null;
}

type PreviewContentData = Event | MarketItem | Page | PreviewPost | Record<string, unknown>;

export default {
    components: {
        CoHeadline,
        CoEvent,
        CoMarketItem,
        CoPage,
        CoPost: () => import('../co-post/CoPost.vue'),
        CoLinkPreview,
    },
    name: 'CoContentPreview',
    props: {
        content: {
            type: Object as PropType<Event | MarketItem | Page | PreviewPost>,
            default: null,
        },
        fetch: {
            type: Object as PropType<Fetch>,
            default: null as Fetch | null,
        },
        variant: {
            type: String,
            default: 'full',
            validator: (value) => ['default', 'full', 'list', 'micro', 'link'].includes(value),
        },
        forceMobileLayout: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            contentLoaded: null,
            fetchFailed: false,
            contentLoadedType: null,
        };
    },
    computed: {
        contentVariant() {
            // When link URLs are not resolved yet, child components still need a supported compact variant.
            return this.variant === 'link' ? 'micro' : this.variant;
        },
        linkPreviewUrl() {
            return this.resolveLinkPreviewUrl(this.contentLoaded || this.content || {});
        },
        // contentTitle derives the title for the link preview variant. It prefers a provided content prop, then falls back to fetched content, and finally to the URL if no title can be found.
        contentTitle() {
            if (this.content && this.content.Title) return this.content.Title;
            if (this.contentLoaded && this.contentLoaded.Title) return this.contentLoaded.Title;
            return null;
        },
    },
    watch: {
        contentFetched: {
            handler() {
                this.contentLoaded = this.processContent();
            },
            deep: true,
        },
        contentLoaded: {
            handler(newVal) {
                const clone = cloneDeep(newVal);
                this.$emit('content-loaded', clone);
            },
            deep: true,
        },
    },
    mounted() {
        this.processContent();
    },
    methods: {
        get(object, path, defaultValue) {
            return get(object, path, defaultValue);
        },
        normalizePreviewUrl(value: string | null | undefined) {
            if (!value || typeof value !== 'string') return null;

            const url = value.trim();
            if (/^mailto:/i.test(url)) return url;

            if (/^https?:\/\//i.test(url)) {
                try {
                    return new URL(url).href;
                } catch {
                    return null;
                }
            }

            if (url.startsWith('/')) {
                return new URL(url, window.location.origin).href;
            }

            return null;
        },
        resolveLinkPreviewUrl(content: PreviewContentData | null) {
            const fetchedLink =
                this.normalizePreviewUrl(get(this.fetch, 'Link', null)) ||
                this.normalizePreviewUrl(get(this.fetch, 'Url', null)) ||
                this.normalizePreviewUrl(get(this.fetch, 'Slug', null));

            if (fetchedLink) return fetchedLink;

            const fullURL = this.normalizePreviewUrl(get(content, 'FullURL', null));
            const slug = get(content, 'Slug', null);
            const type = `${get(content, 'Type', this.contentLoadedType || '') || ''}`.toLowerCase();
            const contentTypeRoutes: Record<string, string> = {
                event: 'event',
                job: 'job',
                project: 'page',
                post: 'post',
            };
            const routeType = contentTypeRoutes[type];

            // Prefer internal content routes, but fall back to FullURL when no slug-based route exists.
            if (!slug || !routeType) return fullURL;

            return new URL(`/${routeType}/${`${slug}`.replace(/^\/+/, '')}`, window.location.origin).href;
        },
        fetchContent({ ID, Slug, Type }: Fetch) {
            return new Promise((resolve, reject) => {
                if (!(ID || Slug)) resolve(null);
                let slug = Slug;
                let type = Type;
                // check if Slug is url and extract Slug and Type
                try {
                    const url = new URL(slug || '');
                    // check if url matches platform url
                    if (url.hostname !== window.location.hostname) {
                        resolve(null);
                    }

                    slug = url.pathname.split('/').pop();
                    switch (url.pathname.split('/')[1].toLowerCase()) {
                        case 'project':
                        case 'page':
                        case 'channels':
                            type = 'project';
                            break;
                        case 'profile':
                            type = 'user';
                            break;
                        case 'event':
                        case 'events':
                            type = 'event';
                            break;
                        case 'job':
                            type = 'job';
                            break;
                        case 'post':
                            type = 'post';
                            break;
                        // case 'profile':
                        //     Type = 'user';
                        //     break;
                        default:
                            reject(new Error('unsupported content type'));
                            break;
                    }
                } catch (error) {
                    // slug is not a URL, continue with the provided slug/type pair
                }
                // set contentLoadedType to Type
                this.contentLoadedType = type;

                let url = ID ? `/${type}/listbyids` : `/${type}/by-slug/${slug}`;
                let mthd = ID ? ('POST' as const) : ('GET' as const);

                if (type === 'event' && ID) {
                    url = `/event/by-id/${ID}`;
                    mthd = 'GET' as const;
                } else if (type === 'post' && ID) {
                    url = `/post/by-id/${ID}`;
                    mthd = 'GET' as const;
                }

                axios({
                    method: mthd,
                    url,
                    withCredentials: true,
                    data: ID ? { IDS: [{ ID }] } : null,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        if (type === 'project') {
                            resolve({
                                ...(get(response, 'data.Projects[0]') || get(response, 'data') || {}),
                            });
                            return;
                        }

                        resolve({
                            ...(get(response, 'data') || {}),
                        });
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        fetchUsers(IDS: Array<string>) {
            return new Promise((resolve, reject) => {
                if (!IDS) resolve(null);
                axios({
                    method: 'POST',
                    url: '/user/listbyids',
                    data: { IDS: map(uniq(IDS), (id) => ({ ID: id })) },
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        let users = get(response, 'data.Users', []);
                        users = map(users, (user) => ({
                            ...user,
                            ...get(user, 'Profile', {}),
                        }));
                        resolve(users);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        fetchInterestedUsers(marketItemID: string) {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve, reject) => {
                if (!marketItemID) resolve([]);
                axios({
                    method: 'GET',
                    url: `/job/get-interested-users/${marketItemID}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(get(response, 'data.Interests', []));
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        fetchChannelInfo(channelID: string) {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve) => {
                if (!channelID) resolve({});
                resolve({});
                // to do: implement fetch channel info by channelID
                // axios({
                //     method: 'GET',
                //     url: '/job/get-interested-users/' + channelID,
                //     withCredentials: true,
                //     headers: {
                //         'Content-Type': 'application/json',
                //     },
                // }).then((response) => {
                //     resolve(get(response, 'data.Interests', []));
                // }).catch((error) => {
                //     reject(error);
                // });
            });
        },
        mapParentType(type: number | string | null | undefined) {
            const normalized = Number(type);
            const parentTypeMap = {
                1: 'event',
                2: 'project',
                3: 'job',
            };

            if (!Number.isNaN(normalized) && parentTypeMap[normalized]) {
                return parentTypeMap[normalized];
            }

            return null;
        },
        async processContent() {
            // if content is not defined or not fetched set return null
            // if (!this.contentFetched || !this.contentFetched.ID) return null;
            let data = {} as any;
            let contentFetched = null;
            let interestFetched = null;
            let channelFetched = null;
            const userList = [];
            let usersFetched = null;

            this.loading = true;
            // 1. checks if content needs to be fetched
            if (this.content) {
                data = {
                    ...this.content,
                };
            } else if (this.fetch) {
                // 2. if so fetch content
                try {
                    contentFetched = await this.fetchContent(this.fetch);
                } catch (error) {
                    console.error('error fetching content', error);
                }
                // if content is not supported set contentLoaded to contentFetched and return
                if (!contentFetched) {
                    this.fetchFailed = true;
                    this.contentLoaded = {};
                    return;
                }
                data = {
                    ...contentFetched,
                };
            }
            // normalize data.Type to lowercase
            data = {
                ...data,
                Type: get(data, 'Type', this.contentLoadedType || '').toLowerCase(),
            };
            // fetch additonal infos if needed
            // interested users if content is a market item
            if (data.Type === 'job') {
                interestFetched = await this.fetchInterestedUsers(data.ID);
                data = {
                    ...data,
                    Interests: interestFetched,
                };
            } else if (data.ChannelID) {
                // channel info if content is a channel
                channelFetched = await this.fetchChannelInfo(data.ID);
                data = {
                    ...data,
                    Type: 'project',
                    Channel: channelFetched,
                };
            }
            // 3. checks if users need to be fetched and build a list of user ids to fetch
            // eslint-disable-next-line default-case
            switch (data.Type) {
                case 'event':
                    // creator, organizers, participants are now populated directly in the api response
                    // so they don't have to be fetched here anymore
                    break;
                case 'post':
                    if (data.AuthorID || data.CreatedBy || data.UserID) {
                        userList.push(data.AuthorID || data.CreatedBy || data.UserID);
                    }
                    break;
                case 'project':
                    map(data.Owner || [], (owner) => {
                        userList.push(owner);
                    });
                    map(data.Contributor || [], (owner) => {
                        userList.push(owner);
                    });
                    break;
            }
            // 4. fetch users
            if (userList.length > 0) {
                try {
                    usersFetched = await this.fetchUsers(userList);
                } catch (error) {
                    console.error('error fetching users', error);
                }
            }
            // 5. map content data according to type
            switch (data.Type) {
                case 'event':
                    data = {
                        ...data,
                        Images:
                            data.Images && typeof get(data, 'Images[0]') !== 'object'
                                ? map(data.Images, (image) => ({
                                      ImageURL: image,
                                  }))
                                : data.Images,
                        Participants: usersFetched || data.Participants,
                        Organizers: usersFetched
                            ? map(data.Organizers, (organizer) => ({
                                  ...usersFetched.find((user) => user.ID === organizer),
                              }))
                            : data.Organizers,
                        Categories: data.EventCategory
                            ? map(data.EventCategory, (category) => ({
                                  Name: category,
                                  Value: category,
                              }))
                            : [],
                        Types: data.EventType
                            ? map(data.EventType, (type) => ({
                                  Name: type,
                                  Value: type,
                              }))
                            : [],
                    };
                    break;
                case 'job':
                    data = {
                        ...data,
                        Images: data.ImageURL
                            ? [
                                  {
                                      ImageURL: data.ImageURL,
                                  },
                              ]
                            : [],
                    };
                    break;
                case 'post': {
                    const authorID = data.AuthorID || data.CreatedBy || data.UserID;
                    const parentType = this.mapParentType(data.ParentType);

                    data = {
                        ...data,
                        Author:
                            data.Author ||
                            (usersFetched ? usersFetched.find((user) => user.ID === authorID) || null : null),
                        Images: map(data.Images || data.ImageURLs || [], (image) =>
                            typeof image === 'string' ? { ImageURL: image } : image
                        ),
                        ParentContent:
                            data.ParentContent ||
                            (data.ParentID && parentType
                                ? {
                                      Fetch: {
                                          ID: data.ParentID,
                                          Type: parentType,
                                      },
                                  }
                                : null),
                    };
                    break;
                }
                // eslint-disable-next-line no-fallthrough
                case 'project':
                    data = {
                        ...data,
                        Images: data.ImageURL && data.ImageURL.length > 0 ? [{ ImageURL: data.ImageURL[0] }] : [],
                        Owners:
                            usersFetched && data.Owner
                                ? map(data.Owner, (owner) => ({
                                      ...usersFetched.find((user) => user.ID === owner),
                                  }))
                                : [],
                        Contributors:
                            usersFetched && data.Contributor
                                ? map(data.Contributor, (contributor) => ({
                                      ...usersFetched.find((user) => user.ID === contributor),
                                  }))
                                : [],
                    };
                    break;
                // eslint-disable-next-line no-fallthrough
                default:
                    break;
            }

            // remove Images urls if they containe '/img/Platform_Gradient'
            data = {
                ...data,
                Images: get(data, 'Images', []).filter((image) => !image?.ImageURL?.includes('/img/Platform_Gradient')),
            };
            this.contentLoaded = data;
            this.loading = false;
        },
    },
};
